import React, { useState } from 'react';
import { Wand2, Download, Trash2, Sparkles, Globe, Loader2, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { VARIANTS } from '../mock';
import { toast } from '../hooks/use-toast';

export default function CustomSpritesSection({
  sprites,
  onRemove,
  onOpenForge,
  onPublish,
  publicSprites = [],
  userId,
  displayName,
  onChangeDisplayName,
}) {
  // A local sprite is considered "already published" only when we can find
  // a public sprite that shares BOTH owner_id and the stable local id. This
  // avoids the old bug where two custom sprites saved with the same default
  // name (e.g. "Custom Sprite") were mistakenly treated as the same entry.
  const isPublished = (s) =>
    !!publicSprites.find(
      (p) => p.owner_id === userId && (p.local_id ? p.local_id === s.id : p.name === s.name)
    );

  if (!sprites?.length) {
    return (
      <section className="max-w-7xl mx-auto px-3 sm:px-6 mt-6">
        <div
          className="rounded-2xl border-[3px] shadow-[0_5px_0_var(--t-card-border)] p-5 md:p-6 flex items-center justify-between gap-4 flex-wrap"
          style={{ background: 'var(--t-card)', borderColor: 'var(--t-card-border)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#ffd900] to-[#ff2bd6] border-2 border-[#3a1064] flex items-center justify-center shrink-0">
              <Wand2 className="h-6 w-6 text-white" strokeWidth={3} />
            </div>
            <div className="min-w-0">
              <div className="font-black text-lg" style={{ color: 'var(--t-text)' }}>
                Sprite Forge — design your own
              </div>
              <div className="text-xs font-bold mt-0.5" style={{ color: 'var(--t-text-soft)' }}>
                Use AI to invent a brand-new Fortnite-style sprite, then publish it to the community.
              </div>
            </div>
          </div>
          <Button
            className="h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
            onClick={onOpenForge}
          >
            <Sparkles className="h-4 w-4 mr-2" /> Open Sprite Forge
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 mt-6">
      <div
        className="rounded-2xl border-[3px] shadow-[0_5px_0_var(--t-card-border)] p-5 md:p-6"
        style={{ background: 'var(--t-card)', borderColor: 'var(--t-card-border)' }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#ffd900] to-[#ff2bd6] border-2 border-[#3a1064] flex items-center justify-center">
              <Wand2 className="h-5 w-5 text-white" strokeWidth={3} />
            </div>
            <div>
              <div className="font-black text-lg" style={{ color: 'var(--t-text)' }}>
                My Custom Sprites <span className="text-[#ff2bd6]">({sprites.length})</span>
              </div>
              <div className="text-xs font-bold" style={{ color: 'var(--t-text-soft)' }}>
                Your AI-forged collection. Make them public to enter the vote.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3a1064]" />
              <Input
                value={displayName || ''}
                onChange={(e) => onChangeDisplayName?.(e.target.value)}
                placeholder="Your display name"
                className="pl-9 h-10 w-[180px] rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064] bg-white"
              />
            </div>
            <Button
              className="h-10 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
              onClick={onOpenForge}
            >
              <Wand2 className="h-4 w-4 mr-2" /> Forge another
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {sprites.map((s) => (
            <CustomCard
              key={s.id}
              sprite={s}
              onRemove={onRemove}
              onPublish={onPublish}
              published={isPublished(s)}
              displayName={displayName}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CustomCard({ sprite, onRemove, onPublish, published, displayName }) {
  const [publishing, setPublishing] = useState(false);
  const variant = VARIANTS[sprite.variant] || VARIANTS.base;

  const download = () => {
    const safeName = sprite.name.replace(/[^a-z0-9-]+/gi, '_') || 'sprite';
    // Convert the data URL to a Blob so we can use a real object URL —
    // browsers (esp. mobile Safari) refuse to download giant `data:` hrefs.
    try {
      const parts = sprite.dataUrl.split(',');
      const meta = parts[0] || '';
      const b64 = parts[1] || '';
      const mimeMatch = meta.match(/data:([^;]+);base64/);
      const mime = (mimeMatch && mimeMatch[1]) || 'image/png';
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}.png`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      return;
    } catch { /* fall through to the tab-open fallback */ }
    // Last-resort fallback for locked-down mobile browsers: open the image
    // in a new tab so the user can long-press → Save to Photos.
    const w = window.open();
    if (w) {
      w.document.write(
        `<title>${safeName}.png</title><body style="margin:0;background:#0b0320;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${sprite.dataUrl}" alt="${safeName}" style="max-width:100%;max-height:100vh"/></body>`
      );
      w.document.close();
    } else {
      window.location.href = sprite.dataUrl;
    }
  };

  const handlePublish = async () => {
    if (published) {
      toast({ title: 'Already public', description: 'Vote it up in the Community Gallery above!' });
      return;
    }
    setPublishing(true);
    try {
      await onPublish({
        name: sprite.name,
        data_url: sprite.dataUrl,
        prompt: sprite.prompt,
        variant: sprite.variant,
        element: sprite.element,
        theme: sprite.theme,
        owner_name: displayName || 'Anonymous',
        local_id: sprite.id,
      });
      toast({
        title: 'Published to gallery!',
        description: 'Friends can now vote on your sprite.',
      });
    } catch (e) {
      toast({ title: 'Publish failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      className="relative rounded-2xl border-[3px] overflow-hidden shadow-[0_5px_0_var(--t-card-border)] hover:-translate-y-1 transition-transform"
      style={{ background: 'var(--t-card)', borderColor: 'var(--t-card-border)' }}
    >
      <div
        className="h-1.5 w-full"
        style={{ background: `linear-gradient(90deg, #ff2bd6, ${variant.color}, #00b8ff)` }}
      />
      <div
        className="relative aspect-square flex items-center justify-center"
        style={{
          background: `radial-gradient(circle at center, ${variant.color}33 0%, transparent 70%), linear-gradient(180deg, #faf7ff 0%, #f3edff 100%)`,
        }}
      >
        <span
          className="absolute top-2 left-2 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md text-white border border-white/40"
          style={{ background: variant.color }}
        >
          {variant.label}
        </span>
        <span className="absolute top-2 right-2 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#ff2bd6] text-white border border-white/30">
          AI
        </span>
        {published && (
          <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-500 text-white border-2 border-white/30">
            <Globe className="h-3 w-3" /> Public
          </span>
        )}
        <img
          src={sprite.dataUrl}
          alt={sprite.name}
          className="h-[82%] w-[82%] object-contain drop-shadow-[0_6px_8px_rgba(58,16,100,0.35)]"
        />
      </div>
      <div className="p-3" style={{ background: 'var(--t-card)' }}>
        <div className="font-black text-sm truncate" style={{ color: 'var(--t-text)' }}>{sprite.name}</div>
        <div className="text-[11px] font-bold mt-0.5 line-clamp-2" style={{ color: 'var(--t-text-soft)' }}>
          {sprite.prompt}
        </div>

        <button
          onClick={handlePublish}
          disabled={publishing || published}
          className={`mt-2 w-full h-9 rounded-xl border-2 text-[11px] font-black uppercase tracking-wide transition-all flex items-center justify-center gap-1.5 shadow-[0_3px_0_var(--t-card-border)] ${
            published
              ? 'bg-emerald-500 text-white cursor-default'
              : 'bg-gradient-to-r from-[#ffd900] via-[#ff2bd6] to-[#00b8ff] text-[#3a1064] hover:opacity-90'
          }`}
          style={{ borderColor: 'var(--t-card-border)' }}
        >
          {publishing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : published ? (
            <><Globe className="h-3.5 w-3.5" /> In Gallery</>
          ) : (
            <><Globe className="h-3.5 w-3.5" /> Make Public</>
          )}
        </button>

        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <button
            onClick={download}
            className="h-8 rounded-lg border-2 border-[#3a1064] bg-white hover:bg-yellow-50 text-[#3a1064] text-[10px] font-black uppercase tracking-wide flex items-center justify-center gap-1 shadow-[0_2px_0_#3a1064]"
          >
            <Download className="h-3.5 w-3.5" /> PNG
          </button>
          <button
            onClick={() => onRemove(sprite.id)}
            className="h-8 rounded-lg border-2 border-rose-300 bg-white hover:bg-rose-50 text-rose-600 text-[10px] font-black uppercase tracking-wide flex items-center justify-center gap-1"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
